<template>
  <v-card flat>
    <v-row no-gutters class="pl-4">
      <v-col cols="12">
        <v-row no-gutters>
          <v-btn color="primary" icon @click="handleGetBack()">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="text-h6" style="vertical-align: middle">
            Pixels
            <learn-more-btn :onClickBtn="handleGoToArticle" />
          </span>
          <v-spacer />
          <v-btn
            v-if="!isEditing && hasEditVideoPerm"
            class="mr-4"
            color="primary"
            @click="handleEditPixel()"
          >
            {{ $t('message.pages.videoPlayerPixels.addPixel') }}</v-btn
          >
        </v-row>
      </v-col>
      <v-col cols="12" class="mt-2 ml-2" v-if="!isEditing">
        <v-card class="overflow-y-auto" flat max-height="240px" width="100%">
          <v-row no-gutters>
            <v-col cols="12" v-for="(pixel, i) in pixelsList" :key="i">
              <v-card class="mt-2 pl-2 mr-6" outlined height="45px">
                <v-row class="fill-height" no-gutters align="center">
                  <v-icon class="pr-2">
                    {{ listIcons[pixel.type] }}
                  </v-icon>
                  <span> {{ getListingTitle(pixel) }}</span>
                  <v-spacer />
                  <v-progress-circular
                    v-if="pixel.isBeingRemoved"
                    class="mr-2"
                    indeterminate
                    color="primary"
                    size="22"
                    width="3"
                  ></v-progress-circular>
                  <v-menu bottom left offset-y v-if="hasEditVideoPerm">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="mr-2" icon v-bind="attrs" v-on="on">
                        <v-icon>mdi-dots-horizontal</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense class="pa-0" width="120px">
                      <v-list-item @click="handleEditPixel(pixel, i)">
                        <v-list-item-title>{{
                          $t('message.pages.videoPlayerButtons.edit')
                        }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="handleRemovePixel(pixel, i)">
                        <v-list-item-title class="error--text">{{
                          $t('message.pages.videoEditModal.delete')
                        }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
      <v-col cols="12" class="ml-2 mt-2" v-else>
        <v-row no-gutters class="pr-4">
          <v-col cols="12">
            <span>
              {{ $t('message.pages.videoPlayerPixels.description') }}
            </span>
          </v-col>
          <v-col cols="12" class="mt-4">
            <v-card
              width="100%"
              height="38px"
              flat
              style="border: 1px solid grey"
            >
              <v-row no-gutters>
                <v-col
                  v-for="(type, index) in pixelsTypes"
                  :key="`${type}--${index}`"
                >
                  <v-btn
                    class="px-0"
                    block
                    :plain="type !== pixelToBeEdited.type"
                    tile
                    style="border-right: 1px solid grey"
                    :color="type === pixelToBeEdited.type ? 'primary' : ''"
                    @click="handleChangePixelType(type)"
                  >
                    <span>{{
                      $t(`message.pages.videoPlayerPixels.platforms.${type}`)
                    }}</span>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-form
            ref="pixelsForm"
            v-model="isPixelsFormValid"
            lazy-validation
            style="width: 100%"
          >
            <v-row no-gutters>
              <v-col
                cols="12"
                class="mt-6"
                v-if="pixelToBeEdited.type === 'custom'"
              >
                <p class="mb-1">
                  {{ $t('message.pages.videoPlayerPixels.labels.name') }}
                </p>
                <v-text-field
                  v-model="pixelToBeEdited.name"
                  dense
                  outlined
                  :placeholder="
                    $t('message.pages.videoPlayerPixels.placeholders.name')
                  "
                  :rules="requiredFieldRule"
                >
                  <template #message="{ message }">
                    <v-icon
                      color="red"
                      size="14"
                      class="iptMessage__error--icon"
                    >
                      mdi-alert-circle
                    </v-icon>
                    <span class="red--text">
                      {{ message }}
                    </span>
                  </template>
                </v-text-field>
              </v-col>
              <v-col
                cols="12"
                class="mt-6"
                v-if="
                  pixelToBeEdited.type === 'fbPixel' ||
                  pixelToBeEdited.type === 'gtagIds'
                "
              >
                <p class="mb-1">ID</p>
                <v-text-field
                  v-if="pixelToBeEdited.type === 'fbPixel'"
                  v-model="pixelToBeEdited.pixelId"
                  v-mask="`################`"
                  dense
                  :prepend-inner-icon="`${listIcons[pixelToBeEdited.type]}`"
                  outlined
                  :placeholder="
                    $t(
                      `message.pages.videoPlayerPixels.placeholders.id.${pixelToBeEdited.type}`
                    )
                  "
                  :rules="requiredFieldRule"
                >
                  <template #message="{ message }">
                    <v-icon
                      color="red"
                      size="14"
                      class="iptMessage__error--icon"
                    >
                      mdi-alert-circle
                    </v-icon>
                    <span class="red--text">
                      {{ message }}
                    </span>
                  </template>
                </v-text-field>
                <v-text-field
                  v-else
                  v-model="pixelToBeEdited.gtagId"
                  dense
                  :prepend-inner-icon="`${listIcons[pixelToBeEdited.type]}`"
                  outlined
                  :placeholder="
                    $t(
                      `message.pages.videoPlayerPixels.placeholders.id.${pixelToBeEdited.type}`
                    )
                  "
                  :rules="requiredFieldRule"
                >
                  <template #message="{ message }">
                    <v-icon
                      color="red"
                      size="14"
                      class="iptMessage__error--icon"
                    >
                      mdi-alert-circle
                    </v-icon>
                    <span class="red--text">
                      {{ message }}
                    </span>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" v-if="pixelToBeEdited.type === 'custom'">
                <v-card
                  width="100%"
                  height="38px"
                  flat
                  style="border: 1px solid grey"
                >
                  <v-row no-gutters>
                    <v-col
                      v-for="(type, index) in eventTimeTypes"
                      :key="`${type}--${index}`"
                    >
                      <v-btn
                        class="px-0"
                        block
                        :plain="type !== pixelToBeEdited.conditionType"
                        tile
                        style="border-right: 1px solid grey"
                        :color="
                          type === pixelToBeEdited.conditionType
                            ? 'primary'
                            : ''
                        "
                        @click="handleChangePixelTimeMode(type)"
                      >
                        <span class="typesText">{{
                          $t(`message.pages.videoPlayerPixels.types.${type}`)
                        }}</span>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
              <v-col
                cols="12"
                class="mt-4"
                v-if="
                  pixelToBeEdited.type === 'custom' &&
                  (pixelToBeEdited.conditionType === 'absolute' ||
                    pixelToBeEdited.conditionType === 'percentage')
                "
              >
                <AntTimePicker
                  v-if="
                    pixelToBeEdited.conditionType === 'absolute' &&
                    !loadings.loadingConditionValueIpt
                  "
                  style="margin-bottom: 26px"
                  :startVal="String(pixelToBeEdited.conditionValue)"
                  :timeVideo="timeVideo"
                  @changedTime="handleEndTimeEvent"
                />
                <v-text-field
                  v-else-if="!loadings.loadingConditionValueIpt"
                  v-model="pixelToBeEdited.conditionValue"
                  dense
                  outlined
                  :rules="requiredFieldRule"
                  :placeholder="'0'"
                  type="number"
                  max="100"
                  min="0"
                  class="pixelsPage__field--percentage"
                >
                  <template #message="{ message }">
                    <v-icon
                      color="red"
                      size="14"
                      class="iptMessage__error--icon"
                    >
                      mdi-alert-circle
                    </v-icon>
                    <span class="red--text">
                      {{ message }}
                    </span>
                  </template>
                </v-text-field>
                <div v-else style="width: 100%; height: 65.97px"></div>
              </v-col>
              <v-col
                cols="12"
                v-if="pixelToBeEdited.type === 'custom'"
                :class="{
                  'mt-4': !(
                    pixelToBeEdited.conditionType === 'absolute' ||
                    pixelToBeEdited.conditionType === 'percentage'
                  ),
                }"
              >
                <p class="mb-1">
                  {{ $t('message.pages.videoPlayerPixels.labels.code') }}
                </p>
                <v-textarea
                  v-model="pixelToBeEdited.script"
                  outlined
                  :placeholder="
                    $t('message.pages.videoPlayerPixels.placeholders.code')
                  "
                  :rules="requiredFieldRule"
                >
                  <template #message="{ message }">
                    <v-icon
                      color="red"
                      size="14"
                      class="iptMessage__error--icon"
                    >
                      mdi-alert-circle
                    </v-icon>
                    <span class="red--text">
                      {{ message }}
                    </span>
                  </template></v-textarea
                >
              </v-col>
            </v-row>
          </v-form>
          <v-col
            cols="12"
            class="d-flex flex-row align-end mt-8"
            v-if="hasEditVideoPerm"
          >
            <v-spacer />
            <v-btn min-width="150" plain @click="handleGetBack()">{{
              $t('message.pages.videoPlayerButtons.cancel')
            }}</v-btn>
            <v-btn
              min-width="150"
              color="primary"
              :disabled="loadings.updatingPixel"
              :loading="loadings.updatingPixel"
              @click="publishPixel"
              >{{ $t('message.pages.videoPlayerButtons.publish') }}</v-btn
            >
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <unsaved-changes-modal />
  </v-card>
</template>

<script>
import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue'
import _ from 'lodash'
import i18n from '@/translations/lang'
import videosApi from '@/services/videosApi.js'
import { mapState, mapActions, mapMutations } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import AntTimePicker from '@/components/AntTimePicker.vue'
import LearnMoreBtn from '@/components/LearnMoreBtn.vue'

export default {
  name: 'VideoPlayerPixels',
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
  },
  components: {
    UnsavedChangesModal,
    AntTimePicker,
    LearnMoreBtn,
  },
  computed: {
    ...mapState({
      video: (state) => state.video.video,
      fbPixel: (state) => state.video.profile.vsl.fbPixel,
      gtagIds: (state) => state.video.profile.vsl.gtagIds,
    }),
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    fbPixelArr: {
      get() {
        return this.fbPixel
      },
      cache: false,
      deep: true,
    },
    gAnalyticsArr: {
      get() {
        return this.gtagIds
      },
      deep: true,
      cache: false,
    },
  },
  data: () => ({
    indexBeingEdited: 0,
    isPixelsFormValid: true,
    pixelsList: [],
    isEditing: false,
    timeVideo: 0,
    requiredFieldRule: [],
    pixelToBeEdited: {},
    pixelToBeEditedInitial: {},
    pixelToBeEditedDefault: {
      name: '',
      id: '',
      pixelId: '',
      gtagId: '',
      type: 'fbPixel',
      script: '',
      conditionValue: 0,
      conditionType: 'absolute',
    },
    profileId: '',
    eventTimeTypes: ['percentage', 'absolute', 'play', 'view'],
    pixelsTypes: ['fbPixel', 'gtagIds', 'custom'],
    loadings: {
      updatingPixel: false,
      loadingConditionValueIpt: false,
    },
    listIcons: {
      fbPixel: 'mdi-facebook',
      gtagIds: 'mdi-google',
      custom: 'mdi-xml',
    },
    articleLinks: {
      pt: 'https://help.pandavideo.com/pt-br/article/como-configurar-os-seus-pixels-14ybymo',
      en: 'https://help.pandavideo.com/en-us/article/how-to-configure-your-pixels-1fesu9c',
    },
  }),
  mounted() {
    this.pixelsList = this.video.config?.customPixels
      ? this.video.config?.customPixels
      : []
    this.$root.$on('discardChangesPixelsPage', () => {
      this.resetData()
      return (this.isEditing = false)
    })
    this.resetData()
    this.timeVideo = this.$store.state.video.video?.length
    this.profileId = this.$store.state.video.video.config?.profile

    this.handleThreatGenerics()
    this.handleUpdatePixelsList()

    this.isEditing = this.pixelsList.length === 0
  },
  methods: {
    ...mapActions('video', [
      'updateProfile',
      'createProfile',
      'setProfileToVideos',
    ]),
    ...mapMutations('video', ['UPDATE_VSL_FB', 'UPDATE_VSL_GOOGLE']),
    getListingTitle(pixel) {
      if (pixel.name) return pixel.name
      if (pixel.pixelId) return pixel.pixelId
      if (pixel.gtagId) return pixel.gtagId
      return ''
    },
    handleGoToArticle() {
      const articleLink =
        this.articleLinks[this.$i18next.resolvedLanguage] ??
        this.articleLinks['en']
      window.open(articleLink, '_blank')
    },
    handleThreatGenerics() {
      this.UPDATE_VSL_FB(this.getStrippedValue(this.fbPixelArr))
      this.UPDATE_VSL_GOOGLE(this.getStrippedValue(this.gAnalyticsArr))
    },
    getStrippedValue(value) {
      if (value) {
        if (typeof value === 'string') {
          return [value]
        }
        if (Array.isArray(value)) {
          return value
        }
      }
      return []
    },
    handleUpdatePixelsList() {
      this.pixelsList = [
        ...this.pixelsList,
        ...this.prepareOldAanalyticsData(this.fbPixelArr, 'fbPixel'),
        ...this.prepareOldAanalyticsData(this.gAnalyticsArr, 'gtagIds'),
      ]
    },
    prepareOldAanalyticsData(arr, type) {
      if (typeof arr === 'string') {
        if (arr) {
          return [arr]
        } else {
          return []
        }
      }
      if (arr.length) {
        return arr.map((item) => {
          const model = _.cloneDeep(this.pixelToBeEditedDefault)
          if (type === 'fbPixel') {
            model.pixelId = item
          } else {
            model.gtagId = item
          }
          model.type = type
          return model
        })
      }
      return arr
    },
    hasSomethingChanged() {
      const initial = _.cloneDeep(this.pixelToBeEditedInitial)
      const current = _.cloneDeep(this.pixelToBeEdited)

      if (
        initial.conditionType === current.conditionType &&
        initial.conditionValue !== current.conditionValue &&
        initial.conditionType === 'absolute'
      ) {
        let initialConditionValue = this.checkPureConditionValues(initial)
        let currentConditionValue = this.checkPureConditionValues(current)
        initial.conditionValue = initialConditionValue
        current.conditionValue = currentConditionValue
      }

      if (
        initial.type !== current.type &&
        !initial.id &&
        !initial.gtagId &&
        !initial.pixelId
      ) {
        initial.type = current.type
      }

      const currentValue =
        current.type === 'fbPixel'
          ? current.pixelId
          : current.type === 'gtagIds'
          ? current.gtagId
          : current.script ?? current.name

      return currentValue && !_.isEqual(initial, current)
    },
    checkPureConditionValues(val) {
      if (typeof val.conditionValue === 'string') {
        if (val.conditionValue.includes(':')) {
          return this.formatTimeToSeconds(val.conditionValue)
        } else {
          return val.conditionValue
        }
      }
      return val.conditionValue
    },
    handleEditPixel(pixel, index = this.pixelsList.length) {
      this.indexBeingEdited = index
      if (![...arguments].length) {
        this.isEditing = true
      } else {
        this.pixelToBeEdited = _.cloneDeep(pixel)
        this.pixelToBeEditedInitial = _.cloneDeep(pixel)
        this.isEditing = true
      }
      this.checkConditionValue()
    },
    checkConditionValue() {
      if (this.pixelToBeEdited.conditionType === 'absolute') {
        this.pixelToBeEdited.conditionValue = this.formatSecondsToTime(
          this.pixelToBeEdited.conditionValue
        )
      }
    },
    formatSecondsToTime(seconds) {
      if (!seconds) return '00:00'
      let res = new Date(seconds * 1000).toISOString()
      if (this.timeVideo < 3600) {
        return res.substring(14, 19)
      }
      return res.substring(11, 19)
    },
    formatTimeToSeconds(time) {
      if (!time) return 0
      let res = time.split(':').reduce((acc, time) => 60 * acc + +time)
      return res
    },
    resetData() {
      this.pixelToBeEdited = _.cloneDeep(this.pixelToBeEditedDefault)
      this.pixelToBeEditedInitial = _.cloneDeep(this.pixelToBeEditedDefault)
    },
    async handleRemovePixel(pixel, index) {
      this.pixelsList[index].isBeingRemoved = true
      this.pixelsList = [...this.pixelsList]

      if (pixel.type !== 'custom') {
        let newPixelsList = []
        if (pixel.type === 'fbPixel') {
          const index = this.fbPixelArr.indexOf(pixel.pixelId)
          this.fbPixelArr.splice(index, 1)
          newPixelsList = _.cloneDeep(this.pixelsList).filter(
            ({ pixelId }) => pixelId !== pixel.pixelId
          )
        } else if (pixel.type === 'gtagIds') {
          const index = this.gAnalyticsArr.indexOf(pixel.gtagId)
          this.gAnalyticsArr.splice(index, 1)
          newPixelsList = _.cloneDeep(this.pixelsList).filter(
            ({ gtagId }) => gtagId !== pixel.gtagId
          )
        }
        await this.handlePublishGenerics(true)
        this.pixelsList = newPixelsList
        this.isEditing = this.pixelsList.length === 0
        return
      }
      const newPixelsList = _.cloneDeep(this.pixelsList).filter(
        ({ id }) => id !== pixel.id
      )
      await videosApi.setPlayerPixel(this.video.id, newPixelsList)
      this.pixelsList = newPixelsList
      this.video.config.customPixels = this.pixelsList
      this.isEditing = this.pixelsList.length === 0
    },
    getConditionValue() {
      if (this.pixelToBeEdited.conditionType === 'percentage') {
        return this.pixelToBeEdited.conditionValue
      } else if (this.pixelToBeEdited.conditionType === 'absolute') {
        return this.getConditionAbsoluteSecondsValue()
      }
    },
    getConditionAbsoluteSecondsValue() {
      let seconds
      if (typeof this.pixelToBeEdited.conditionValue === 'string') {
        seconds = this.formatTimeToSeconds(this.pixelToBeEdited.conditionValue)
      } else {
        seconds = this.pixelToBeEdited.conditionValue
      }
      return seconds
    },
    getScriptWithoutScriptTag() {
      this.pixelToBeEdited.script = this.pixelToBeEdited.script
        .replaceAll('<script>', '')
        .replaceAll(`<\/script>`, '') // eslint-disable-line
      return this.pixelToBeEdited.script
    },
    async handlePublishCustom() {
      try {
        let name = this.pixelToBeEdited.name
        if (
          !this.pixelToBeEdited.name &&
          this.pixelToBeEditedInitial.type !== 'custom'
        ) {
          if (this.pixelToBeEditedInitial.type === 'fbPixel') {
            name = this.pixelToBeEdited.pixelId
          } else {
            name = this.pixelToBeEdited.gtagId
          }
        }
        const pixelObj = {
          name,
          script: this.getScriptWithoutScriptTag(),
          conditionType: this.pixelToBeEdited.conditionType,
          conditionValue: this.getConditionValue(),
          id: this.pixelToBeEdited.id ? this.pixelToBeEdited.id : uuidv4(),
          type: this.pixelToBeEdited.type,
        }
        this.pixelsList[this.indexBeingEdited] = pixelObj
        const listToBePosted = this.pixelsList.filter(
          (item) => item.type === 'custom'
        )
        await videosApi.setPlayerPixel(this.video.id, listToBePosted)
      } catch (err) {
        console.error(err)
      }
    },
    async checkProfile() {
      if (!this.video.config.profile) {
        const { data } = await this.createProfile(this.video.id)
        this.video.config.profile = data.id
        this.profileId = data.id
        await this.setProfileToVideos([this.profileId, this.video.id])
      }
    },
    loadCurrentPixelOnGenerics() {
      if (this.pixelToBeEdited.type === 'fbPixel') {
        const index = this.fbPixelArr.indexOf(
          this.pixelToBeEditedInitial.pixelId
        )
        if (index !== -1) {
          this.fbPixelArr[index] = this.pixelToBeEdited.pixelId
        } else {
          this.fbPixelArr.push(this.pixelToBeEdited.pixelId)
        }
      } else if (this.pixelToBeEdited.type === 'gtagIds') {
        const index = this.gAnalyticsArr.indexOf(
          this.pixelToBeEditedInitial.gtagId
        )
        if (index !== -1) {
          this.gAnalyticsArr[index] = this.pixelToBeEdited.gtagId
        } else {
          this.gAnalyticsArr.push(this.pixelToBeEdited.gtagId)
        }
      }
    },
    async handlePublishGenerics(isReplacing = false) {
      try {
        await this.checkProfile()
        if (!isReplacing) this.loadCurrentPixelOnGenerics()

        const controls = this.$store.state.video.profile.controls
        const colors = this.$store.state.video.profile.colors
        const vsl = this.$store.state.video.profile.vsl

        vsl['fbPixel'] = this.fbPixelArr.filter((el) => el !== '')
        vsl['gtagIds'] = this.gAnalyticsArr.filter((el) => el !== '')

        await this.updateProfile([
          this.profileId,
          {
            controls: controls,
            colors: colors,
            vsl: vsl,
          },
        ])
        if (!isReplacing) this.addCurrentGenericToTheList()
      } catch (e) {
        console.error(e)
      }
    },
    addCurrentGenericToTheList() {
      const pixelObj = _.cloneDeep(this.pixelToBeEditedDefault)
      pixelObj.type = this.pixelToBeEdited.type
      if (pixelObj.type === 'fbPixel') {
        pixelObj.pixelId = this.pixelToBeEdited.pixelId
      } else {
        pixelObj.gtagId = this.pixelToBeEdited.gtagId
      }
      this.pixelsList[this.indexBeingEdited] = pixelObj
    },
    async validateForm() {
      this.requiredFieldRule = [
        (v) => {
          if (typeof v === 'number') {
            if (v >= 0) {
              return true
            }
            return false
          }
          return !!v || i18n.t('message.requiredFieldMessage')
        },
      ]
      await this.$refs.pixelsForm.validate()
    },
    async publishPixel() {
      await this.validateForm()

      if (!this.isPixelsFormValid) return
      this.loadings.updatingPixel = true

      if (this.pixelToBeEdited.type === 'custom') {
        this.pixelToBeEdited.conditionValue = parseInt(
          this.pixelToBeEdited.conditionValue
        )
        await this.handlePublishCustom()
      } else {
        await this.handlePublishGenerics()
      }

      if (
        this.pixelToBeEditedInitial.type !== this.pixelToBeEdited.type &&
        (this.pixelToBeEditedInitial.id ||
          this.pixelToBeEditedInitial.pixelId ||
          this.pixelToBeEditedInitial.gtagId)
      ) {
        if (this.pixelToBeEditedInitial.type === 'custom') {
          // when it was Custom and now is FB
          const listToBePosted = this.pixelsList.filter(
            (item) =>
              item.id !== this.pixelToBeEditedInitial.id &&
              item.type === 'custom'
          )
          await videosApi.setPlayerPixel(this.video.id, listToBePosted)
        } else {
          // when it was FB and now is Custom
          let updateGenerics = false
          if (this.pixelToBeEditedInitial.type === 'fbPixel') {
            const index = this.fbPixelArr.indexOf(
              this.pixelToBeEditedInitial.pixelId
            )
            if (index !== -1) {
              this.fbPixelArr.splice(index, 1)
              updateGenerics = true
            }
          } else if (this.pixelToBeEditedInitial.type === 'gtagIds') {
            let index = this.gAnalyticsArr.indexOf(this.pixelToBeEdited.gtagId)
            if (index !== -1) {
              this.gAnalyticsArr.splice(index, 1)
              updateGenerics = true
            }
          }
          if (updateGenerics) await this.handlePublishGenerics(true)
        }
      } else if (this.pixelToBeEdited.type === 'custom') {
        await this.handlePublishGenerics(true)
      }

      this.loadings.updatingPixel = false
      this.handleGetBack(true)
    },
    handleGetBack(forceExit = false) {
      if (this.hasSomethingChanged() && !forceExit) {
        if (this.isEditing) {
          this.$root.$emit('unsavedChangesModal', false, false, false, true)
          return
        }
      }
      if (this.isEditing && (forceExit || this.pixelsList.length !== 0)) {
        this.resetData()
        return (this.isEditing = false)
      }
      this.handleBack()
    },
    handleChangePixelType(type) {
      this.pixelToBeEdited.type = type
      this.$refs.pixelsForm.reset()
    },
    handleEndTimeEvent(timeString) {
      this.pixelToBeEdited.conditionValue = this.formatTimeToSeconds(timeString)
    },
    async handleChangePixelTimeMode(timeType) {
      if (timeType !== this.pixelToBeEdited.conditionType) {
        this.loadings.loadingConditionValueIpt = true
        if (
          timeType === 'percentage' &&
          this.pixelToBeEdited.conditionType === 'absolute'
        ) {
          let seconds = this.getConditionAbsoluteSecondsValue()
          if (!seconds) {
            this.pixelToBeEdited.conditionValue = 0
          } else {
            this.pixelToBeEdited.conditionValue = Math.round(
              (seconds / this.timeVideo) * 100
            )
          }
        } else if (
          timeType === 'absolute' &&
          this.pixelToBeEdited.conditionType === 'percentage'
        ) {
          this.pixelToBeEdited.conditionValue = this.formatSecondsToTime(
            Math.round(
              (this.pixelToBeEdited.conditionValue * this.timeVideo) / 100
            )
          )
        }
        this.loadings.loadingConditionValueIpt = false
      }
      this.$nextTick(() => {
        this.pixelToBeEdited.conditionType = timeType
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.typesText {
  font-size: 11px;
}
</style>

<style>
.pixelsPage__field--percentage .v-text-field__slot::after {
  position: absolute;
  top: 9px;
  color: white !important;
  right: 0.5em;
  transition: all 0.05s ease-in-out;
  content: '%';
}

.pixelsPage__field--percentage .v-text-field__slot:hover::after,
.pixelsPage__field--percentage .v-text-field__slot:focus-within::after {
  right: 1.5em;
}
</style>
